<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1200px"
    scrollable
  >
    <v-card class="geo-dialog-card">
      <!-- Header bar - primary green -->
      <v-toolbar dense flat color="primary" class="white--text">
        <span class="geo-dialog-title">{{ $t('finance.loanApplications.viewGeolocation') }}</span>
        <v-spacer />
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-6">
        <!-- Farm details - left side of page, label above value, black text -->
        <div class="farm-details-section">
          <h3 class="section-title">
            {{ $t('finance.loanApplications.farmDetails') }}
          </h3>
          <v-row class="farm-details-row">
            <v-col cols="12" md="4" class="farm-details-col farm-details-left">
              <div class="geo-detail-item">
                <span class="geo-detail-label">{{ $t('finance.loanApplications.farm') }}</span>
                <span class="geo-detail-value">{{ farmName || '-' }}</span>
              </div>
              <div class="geo-detail-item">
                <span class="geo-detail-label">{{ $t('finance.loanApplications.country') }}</span>
                <span class="geo-detail-value">{{ country || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="farm-details-col farm-details-right">
              <div class="geo-detail-item">
                <span class="geo-detail-label">{{ $t('finance.loanApplications.farmer') }}</span>
                <span class="geo-detail-value">{{ farmerName || '-' }}</span>
              </div>
              <div class="geo-detail-item">
                <span class="geo-detail-label">{{ $t('finance.loanApplications.areaHa') }}</span>
                <span class="geo-detail-value">{{ areaHa || '-' }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Geolocation: same design as Farm Details - title left, Polygon/Circular button right -->
        <div class="geolocation-section">
          <div class="geolocation-header d-flex align-center justify-space-between">
            <h3 class="section-title geolocation-title">
              {{ $t('finance.loanApplications.geolocation') }}
            </h3>
          <v-btn outlined small class="polygon-btn grey--text text--darken-1" depressed @click="drawMode = drawMode === 'polygon' ? 'circular' : 'polygon'">
            {{ drawMode === 'polygon' ? $t('finance.loanApplications.polygon') : $t('finance.loanApplications.circular') }}
          </v-btn>
          </div>
        </div>

        <!-- Map area -->
        <div class="map-wrapper">
          <div class="search-container">
            <v-text-field 
              ref="searchInput"
              prepend-inner-icon="mdi-magnify" 
              v-model="search"
              :placeholder="$t('finance.loanApplications.typeYourLocationNameAddress')"
              outlined 
              dense 
              class="map-search"
              @keyup.enter="searchLocation"
              @input="onSearchInput"
              @focus="showAutocomplete = true"
              @blur="handleSearchBlur"
            />
            <!-- Autocomplete dropdown -->
            <v-card v-if="showAutocomplete && autocompleteResults.length > 0" class="autocomplete-list" elevation="5">
              <v-list dense>
                <v-list-item
                  v-for="(result, index) in autocompleteResults"
                  :key="index"
                  @click="selectAutocompleteResult(result)"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon>mdi-map-marker</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ result.description }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </div>
          <div class="map-inner">
            <div
              ref="googleMapEl"
              class="location-dialog-google-map"
              role="application"
              :aria-label="$t('finance.loanApplications.geolocation')"
            />
          </div>
          <v-btn 
            class="mx-2 float-right geo-current-location-btn" 
            fab 
            dark 
            small 
            color="white"
            @click="gotoCurrentLocation"
          >
            <v-icon dark color="gray">
              mdi-crosshairs-gps
            </v-icon>
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end px-6 pb-6">
        <v-btn outlined color="primary" class="close-btn-text" @click="$emit('input', false)">
          {{ $t('finance.loanApplications.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';

export default {
  name: 'LocationDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    farmName: {
      type: String,
      default: ''
    },
    farmerName: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: ''
    },
    areaHa: {
      type: [Number, String],
      default: null
    },
    initialLatitude: {
      type: [Number, String],
      default: null
    },
    initialLongitude: {
      type: [Number, String],
      default: null
    }
  },

  mounted() {
    this.applyInitialCoordinatesFromPropsOrGeo();
  },

  data() {
    return {
      drawMode: 'polygon', // 'polygon' | 'circular' - toggled by the button
      isFormValid: false,
      longitude: '',
      latitude: '',
      radius: 0,
      additionalNotes: '',
      search: '',
      mapCenter: { lat: -12.0464, lng: -77.0428 },
      markerPosition: { lat: -12.0464, lng: -77.0428 },
      mapInstance: null,
      markerInstance: null,
      mapClickListener: null,
      markerDragListener: null,
      breadcrumbs: [
        { text: 'Dashboard', disabled: true, href: '/' },
        { text: 'Carbon Credit', disabled: true, href: '/carbon-credit' },
        { text: 'Plot Tree Mapping', disabled: true, href: '/carbon-credit/plot-tree-mapping' },
        { text: 'Choose Farm Location', disabled: true, href: '#' },
      ],
      longitudeRules: [
        v => !!v || 'Longitude is required',
        v => !isNaN(v) || 'Longitude must be a number',
        v => (v >= -180 && v <= 180) || 'Longitude must be between -180 and 180'
      ],
      latitudeRules: [
        v => !!v || 'Latitude is required',
        v => !isNaN(v) || 'Latitude must be a number',
        v => (v >= -90 && v <= 90) || 'Latitude must be between -90 and 90'
      ],
      showAutocomplete: false,
      autocompleteResults: [],
      searchTimeout: null
    }
  },

  watch: {
    value: {
      immediate: true,
      async handler(open) {
        if (open) {
          this.applyCoordsFromInitialProps();
          await this.$nextTick();
          await this.ensureMapInitialized();
          this.scheduleMapResize();
        } else {
          this.destroyNativeMap();
        }
      },
    },
    initialLatitude: 'onInitialCoordsPropChange',
    initialLongitude: 'onInitialCoordsPropChange',
    longitude(newVal) {
      if (newVal && this.latitude) {
        this.updateMarkerPosition();
        this.syncNativeMapPosition();
      }
    },
    latitude(newVal) {
      if (newVal && this.longitude) {
        this.updateMarkerPosition();
        this.syncNativeMapPosition();
      }
    },
  },

  computed: {
    loading() {
      return this.mixingIsLoading;
    }
  },

  mixins: [loadingMixin],
  methods: {
    async ensureMapsLibraryLoaded() {
      const loader = getGoogleMapsLoader();
      await loader.load();
    },
    applyInitialCoordinatesFromPropsOrGeo() {
      const lat = parseFloat(this.initialLatitude);
      const lng = parseFloat(this.initialLongitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        this.latitude = lat.toFixed(6);
        this.longitude = lng.toFixed(6);
        this.mapCenter = { lat, lng };
        this.markerPosition = { lat, lng };
      } else {
        this.initializeMap();
      }
    },
    applyCoordsFromInitialProps() {
      const lat = parseFloat(this.initialLatitude);
      const lng = parseFloat(this.initialLongitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        this.latitude = lat.toFixed(6);
        this.longitude = lng.toFixed(6);
        this.mapCenter = { lat, lng };
        this.markerPosition = { lat, lng };
      }
    },
    onInitialCoordsPropChange() {
      if (!this.value) return;
      this.applyCoordsFromInitialProps();
      this.syncNativeMapPosition();
      this.scheduleMapResize();
    },
    destroyNativeMap() {
      if (window.google && window.google.maps && this.mapInstance) {
        if (this.mapClickListener) {
          window.google.maps.event.removeListener(this.mapClickListener);
          this.mapClickListener = null;
        }
        if (this.markerDragListener) {
          window.google.maps.event.removeListener(this.markerDragListener);
          this.markerDragListener = null;
        }
      }
      if (this.markerInstance) {
        this.markerInstance.setMap(null);
        this.markerInstance = null;
      }
      this.mapInstance = null;
    },
    async ensureMapInitialized() {
      await this.$nextTick();
      let el = this.$refs.googleMapEl;
      if (!el) {
        await this.$nextTick();
        el = this.$refs.googleMapEl;
      }
      if (!el) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        el = this.$refs.googleMapEl;
      }
      if (!el) return;
      await this.ensureMapsLibraryLoaded();
      const google = window.google;
      const lat = parseFloat(this.latitude) || this.markerPosition.lat;
      const lng = parseFloat(this.longitude) || this.markerPosition.lng;
      const center = { lat, lng };
      if (!this.mapInstance) {
        this.mapInstance = new google.maps.Map(el, {
          center,
          zoom: 18,
          mapTypeId: 'satellite',
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'cooperative',
        });
        this.markerInstance = new google.maps.Marker({
          position: center,
          map: this.mapInstance,
          draggable: true,
          clickable: false,
        });
        this.mapClickListener = this.mapInstance.addListener('click', (e) => {
          const nlat = e.latLng.lat();
          const nlng = e.latLng.lng();
          this.latitude = nlat.toFixed(6);
          this.longitude = nlng.toFixed(6);
          this.markerPosition = { lat: nlat, lng: nlng };
          if (this.markerInstance) {
            this.markerInstance.setPosition({ lat: nlat, lng: nlng });
          }
          this.reverseGeocode({ lat: nlat, lng: nlng });
        });
        this.markerDragListener = this.markerInstance.addListener('dragend', (e) => {
          const nlat = e.latLng.lat();
          const nlng = e.latLng.lng();
          this.latitude = nlat.toFixed(6);
          this.longitude = nlng.toFixed(6);
          this.markerPosition = { lat: nlat, lng: nlng };
        });
      } else {
        this.syncNativeMapPosition();
      }
    },
    syncNativeMapPosition() {
      if (!this.mapInstance || !this.markerInstance) return;
      const lat = parseFloat(this.latitude);
      const lng = parseFloat(this.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const pos = { lat, lng };
      this.mapInstance.setCenter(pos);
      this.markerInstance.setPosition(pos);
      this.markerPosition = pos;
      this.mapCenter = pos;
    },
    scheduleMapResize() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.mapInstance && window.google && window.google.maps) {
            window.google.maps.event.trigger(this.mapInstance, 'resize');
            const lat = parseFloat(this.latitude);
            const lng = parseFloat(this.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              this.mapInstance.setCenter({ lat, lng });
            }
          }
        }, 150);
      });
    },
    goBack() {
      this.$router.go(-1);
    },

    onCancel() {
      this.$router.push({
        name: 'treePlotAdd',
      });
    },

    updateMarkerPosition() {
      if (this.longitude && this.latitude) {
        this.markerPosition = {
          lat: parseFloat(this.latitude),
          lng: parseFloat(this.longitude),
        };
      }
    },

    async searchLocation() {
      if (!this.search.trim()) return;

      this.startLoading();

      try {
        await this.ensureMapsLibraryLoaded();

        const geocoder = new window.google.maps.Geocoder();
        
        // Search for the address
        geocoder.geocode({ address: this.search }, (results, status) => {
          this.stopLoading();
          
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            
            // Update form fields
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            
            // Move marker to the searched location
            this.markerPosition = { lat, lng };
            
            this.mapCenter = { lat, lng };
            this.syncNativeMapPosition();
          } else {
            this.$notify({
              title: 'Error',
              text: 'Location not found. Please try a different search term.',
              type: 'error'
            });
          }
        });
      } catch (error) {
        this.stopLoading();
        console.error('Error searching location:', error);
        this.$notify({
          title: 'Error',
          text: 'Error searching location. Please try again.',
          type: 'error'
        });
      }
    },

    initializeMap() {
      // Try to get current location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.syncNativeMapPosition();
          },
          (error) => {
            console.log('Geolocation error:', error);
          }
        );
      }
    },

    onSave() {
      if (!this.$refs.locationForm.validate()) {
        this.$notify({
          title: 'Error',
          text: 'Please fill in all required fields',
          type: 'error'
        });
        return;
      }

      const farmLocation = {
        longitude: parseFloat(this.longitude),
        latitude: parseFloat(this.latitude),
        address: this.search, // Assuming 'search' holds the address
        areaSize: this.areaSize,
        areaUnit: this.areaUnit
      };

      // Commit to Vuex store
      this.$store.dispatch('carbonCredit/setFarmLocation', farmLocation);
      
      // Go back to the previous page
      this.$router.go(-1);
    },

    gotoCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            this.syncNativeMapPosition();
            this.reverseGeocode({ lat, lng });
          },
          (error) => {
            console.log('Geolocation error:', error);
            this.$notify({
              title: 'Error',
              text: 'Unable to get current location. Please check your browser permissions.',
              type: 'error'
            });
          }
        );
      } else {
        this.$notify({
          title: 'Error',
          text: 'Geolocation is not supported by this browser.',
          type: 'error'
        });
      }
    },

    onSearchInput(value) {
      const q = typeof value === 'string' ? value : (value && value.target && value.target.value) || '';
      if (q.length < 3) {
        this.autocompleteResults = [];
        return;
      }

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        this.performAutocompleteSearch(q);
      }, 300);
    },

    handleSearchBlur() {
      // Delay hiding autocomplete to allow for clicks
      setTimeout(() => {
        this.showAutocomplete = false;
      }, 200);
    },

    selectAutocompleteResult(result) {
      this.search = result.description;
      this.showAutocomplete = false;
      
      // Get the place details
      this.getPlaceDetails(result.place_id);
    },

    async performAutocompleteSearch(query) {
      try {
        await this.ensureMapsLibraryLoaded();

        const service = new window.google.maps.places.AutocompleteService();
        
        service.getPlacePredictions({
          input: query,
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: [] } // Search worldwide
        }, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.autocompleteResults = predictions;
          } else {
            this.autocompleteResults = [];
          }
        });
      } catch (error) {
        console.error('Autocomplete search error:', error);
        this.autocompleteResults = [];
      }
    },

    async getPlaceDetails(placeId) {
      try {
        await this.ensureMapsLibraryLoaded();
        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        
        service.getDetails({
          placeId: placeId,
          fields: ['geometry', 'formatted_address', 'address_components']
        }, (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            
            // Update form fields
            this.latitude = lat.toFixed(6);
            this.longitude = lng.toFixed(6);
            
            // Move marker to the selected location
            this.markerPosition = { lat, lng };
            
            this.mapCenter = { lat, lng };
            this.search = place.formatted_address;
            this.syncNativeMapPosition();
          }
        });
      } catch (error) {
        console.error('Error getting place details:', error);
        this.$notify({
          title: 'Error',
          text: 'Error getting location details. Please try again.',
          type: 'error'
        });
      }
    },

    async reverseGeocode(location) {
      try {
        await this.ensureMapsLibraryLoaded();
      } catch (e) {
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location }, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            this.search = results[0].formatted_address;
          } else {
            this.search = 'Address not found';
          }
        } else {
          console.error('Geocoder failed due to: ' + status);
          this.search = 'Address lookup failed';
        }
      });
    },
  },
  beforeDestroy() {
    this.destroyNativeMap();
  },
};
</script>

<style scoped>
.map-wrapper {
  position: relative;
  width: 100%;
  height: 60vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.map-search {
  position: absolute;
  top: 14%;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  z-index: 2;
}

.search-container {
  position: absolute;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
  top: 18%;
  z-index: 3;
}

.map-inner {
  width: 96%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.location-dialog-google-map {
  width: 100%;
  height: 100%;
  min-height: 45vh;
  background: #e0e0e0;
}

::v-deep .v-input__slot {
  background: #fff !important;
  border-radius: 8px !important;
}

::v-deep .v-text-field__details {
  margin-bottom: 8px;
}

::v-deep .v-select__selections {
  padding-top: 8px;
}

::v-deep .v-text-field .v-input__control{
  border-radius: 4px !important;
}

::v-deep .bg-primary .v-input__slot{
  background-color: #03BD72 !important;
  border-color: #03BD72 !important;
}
::v-deep .v-select.v-input--dense .v-select__selection--comma{
  color: #fff !important;
  border-color: #03BD72 !important;
}

::v-deep .bg-primary .v-icon.v-icon{
  color: #fff !important;
}

.autocomplete-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;
  width: 60%;
  margin: auto;
  margin-top: 5%;
}
.autocomplete-list .v-list {
  padding: 0;
}

.autocomplete-item {
  cursor: pointer;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  margin-top: 30px;
}

.autocomplete-item:hover {
  background-color: #f5f5f5;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item .v-list-item__content {
  padding: 0;
}

.autocomplete-item .v-list-item__title {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

.geo-dialog-card {
  border-radius: 8px;
  overflow: hidden;
}

.geo-dialog-title {
  font-weight: 600;
  font-size: 16px;
}

/* Shared section title: Farm Details & Geolocation - same design, black */
.section-title {
  font-weight: 700;
  font-size: 1.125rem;
  color: #000;
  margin-bottom: 16px !important;
}

/* Farm details: left-aligned block, label above value, all text black */
.farm-details-section {
  text-align: left;
  margin-bottom: 28px; /* substantial gap before Geolocation */
  color: #000;
}

.farm-details-row {
  margin-top: 0;
}

.farm-details-col {
  padding-top: 0;
  padding-bottom: 0;
}

.farm-details-left {
  padding-right: 24px;
}

/* Farmer & Area column pulled left (narrower so content sits more left) */
.farm-details-right {
  padding-left: 8px;
}

/* Each detail: label on top, value below */
.geo-detail-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px; /* equal vertical gap between rows */
}

.geo-detail-item:last-child {
  margin-bottom: 0;
}

.geo-detail-label {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin-bottom: 4px;
}

.geo-detail-value {
  font-size: 14px;
  font-weight: 400;
  color: #000;
}

.geo-current-location-btn {
  position: absolute;
  right: 16px;
  bottom: 16px;
}

/* Geolocation section: same design as Farm Details (title style, spacing) */
.geolocation-section {
  margin-bottom: 16px; /* gap below Geolocation line before map */
  color: #000;
}

.geolocation-header {
  flex-wrap: nowrap;
  min-height: 32px;
}

.geolocation-title {
  flex-shrink: 0;
  margin-bottom: 0 !important; /* row has title + button on one line */
}

.polygon-btn {
  flex-shrink: 0;
  border-color: #9e9e9e !important;
  color: #616161 !important;
}

.close-btn-text {
  text-transform: uppercase;
}
</style>
